/**
 * Hook for fetching a run's total token usage and estimated cost.
 *
 * Superadmin-only data — callers must gate rendering (and pass `enabled`)
 * behind `useAuth().user?.isSuperadmin` so the request is never issued for
 * non-superadmins.
 *
 * @module hooks/useWorkflowRunTokenUsage
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config.js';
import { toApiError } from '../lib/api-error';

interface WorkflowRunTokenTotals {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  costCents: number;
}

interface RunDiagnosticsTimelineResponse {
  ok: boolean;
  data?: unknown[];
  totals?: WorkflowRunTokenTotals;
  error?: string;
}

/**
 * Fetch total tokens + estimated cost for a workflow run.
 *
 * @param runId - The run ID
 * @param orgId - The run's org ID
 * @param enabled - Whether to fetch (gate behind superadmin status)
 */
export function useWorkflowRunTokenUsage(
  runId: string | undefined,
  orgId: string | undefined,
  enabled: boolean
) {
  return useQuery<WorkflowRunTokenTotals | null>({
    queryKey: ['workflow-run-token-usage', runId, orgId],
    queryFn: async () => {
      if (!runId) return null;

      const params = new URLSearchParams();
      if (orgId) params.set('orgId', orgId);

      const res = await fetch(
        `${API_URL}/superadmin/workflow-diagnostics/run/${runId}?${params.toString()}`,
        { credentials: 'include' }
      );

      if (!res.ok) {
        throw await toApiError(res, 'Failed to load run token usage');
      }

      const json: RunDiagnosticsTimelineResponse = await res.json();

      if (!json.ok || !json.totals) return null;

      return json.totals;
    },
    enabled: enabled && Boolean(runId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
